import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import { formatLongDateWithTime } from "utilities";
import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import { ButtonSecondary } from "components/Atoms/Button";
import { AccessTypesList } from "./AccessTypesList";
import { useOpenNewAccessTypeSidePanel } from "./NewAccessTypeSidePanel";

interface GuestlistConfigFormState {
  closingDate: moment.Moment;
  allowMultipleGuestsPerName: boolean;
  guestEmailAddressFormBehavior: server.dto.FormElementBehaviors;
  guestMobileNumberFormBehavior: server.dto.FormElementBehaviors;
  guestPassportNumberFormBehavior: server.dto.FormElementBehaviors;
  guestNotesFormBehavior: server.dto.FormElementBehaviors;
}

interface GuestlistConfigSidePanelDetailsProps {
  data: server.dto.GuestlistSidePanel;
}

export const GuestlistConfigSidePanelDetails = ({ data }: GuestlistConfigSidePanelDetailsProps) => {
  const openNewAccessTypeSidePanel = useOpenNewAccessTypeSidePanel();
  const [formState, setFormState] = useState<GuestlistConfigFormState>({
    closingDate: moment.utc(data.configuration.deadlineUtc),
    allowMultipleGuestsPerName: data.configuration.multipleGuestsPerName,
    guestEmailAddressFormBehavior: data.configuration.guestEmailAddressFormElementBehavior,
    guestMobileNumberFormBehavior: data.configuration.guestMobileNumberFormElementBehavior,
    guestPassportNumberFormBehavior: data.configuration.guestPassportNumberFormElementBehavior,
    guestNotesFormBehavior: data.configuration.guestNotesFormElementBehavior,
  });

  useEffect(() => {
    setFormState({
      closingDate: moment.utc(data.configuration.deadlineUtc),
      allowMultipleGuestsPerName: data.configuration.multipleGuestsPerName,
      guestEmailAddressFormBehavior: data.configuration.guestEmailAddressFormElementBehavior,
      guestMobileNumberFormBehavior: data.configuration.guestMobileNumberFormElementBehavior,
      guestPassportNumberFormBehavior: data.configuration.guestPassportNumberFormElementBehavior,
      guestNotesFormBehavior: data.configuration.guestNotesFormElementBehavior,
    });
  }, [data.configuration]);

  const onValueChanged = (key: string, value: any) => {
    if (key === "closingDate") {
      setFormState(prevState => ({
        ...prevState,
        closingDate: value,
      }));
    } else if (key === "allowMultipleGuestsPerName") {
      setFormState(prevState => ({
        ...prevState,
        allowMultipleGuestsPerName: value,
      }));
    } else if (key === "email") {
      setFormState(prevState => ({
        ...prevState,
        guestEmailAddressFormBehavior: value,
      }));
    } else if (key === "mobileNumber") {
      setFormState(prevState => ({
        ...prevState,
        guestMobileNumberFormBehavior: value,
      }));
    } else if (key === "passport") {
      setFormState(prevState => ({
        ...prevState,
        guestPassportNumberFormBehavior: value,
      }));
    } else if (key === "notes") {
      setFormState(prevState => ({
        ...prevState,
        guestNotesFormBehavior: value,
      }));
    }
  }

  const getUpdateInstruction = (state: GuestlistConfigFormState): server.dto.UpdateGuestlistConfigurationInstruction => ({
    allowMultipleGuestsPerName: state.allowMultipleGuestsPerName,
    deadlineUtc: state.closingDate.toDate(),
    guestEmailAddressFormBehavior: state.guestEmailAddressFormBehavior,
    guestlistId: data.configuration.guestlistId,
    guestMobileNumberFormBehavior: state.guestMobileNumberFormBehavior,
    guestNotesFormBehavior: state.guestNotesFormBehavior,
    guestPassportNumberFormBehavior: state.guestPassportNumberFormBehavior,
  });

  const cannotEditAllowMultipleGuestsPerName = data.configuration.hasGuestsWithMultipleGuestsPerName;
  const cannotSetEmailAddressAsMandatory = data.configuration.hasGuestsWithoutEmailAddress;
  const cannotSetMobileNumberAsMandatory = data.configuration.hasGuestsWithoutMobileNumber;
  const cannotSetPassportNumberAsMandatory = data.configuration.hasGuestsWithoutPassportNumber;
  const cannotSetNotesAsMandatory = data.configuration.hasGuestsWithoutNotes;

  return <SidePanelDetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    fields={[{
      key: "settings",
      type: "regular",
      title: RESX.Guestlist.ClosingDate,
      fields: [{
        key: "closingDate",
        label: RESX.Guestlist.ClosingDate,
        render: () => <div className="flex-column">
          <div>{formatLongDateWithTime(formState.closingDate)}</div>
        </div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
            value={value}
            onChange={v => onChange(v)}
            validatorKey="closingDate"
            minDate={moment.utc(serverReference.constants.appointment.minDate)}
            maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
            onEnter={submit}
            onEsc={cancel}
          />,
          type: "confirmOrCancelButton",
          fullWidth: true,
          initialValue: formState.closingDate,
          submitChange: (closingDate: moment.Moment) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateInstruction({ ...formState, closingDate })
          ),
        }
      }]
    }, {
      key: "guestForm",
      type: "regular",
      title: RESX.Guestlist.Guest,
      fields: [{
        key: "allowMultipleGuestsPerName",
        label: RESX.Guestlist.AllowPlus1,
        render: () => <YesNoText value={formState.allowMultipleGuestsPerName} />,
        inPageEdit: cannotEditAllowMultipleGuestsPerName ? undefined : {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={v => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.allowMultipleGuestsPerName,
          submitChange: (allowMultipleGuestsPerName: boolean) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateInstruction({ ...formState, allowMultipleGuestsPerName }),
          ),
        },
      }, {
        key: "email",
        label: RESX.Email.EmailAddress,
        render: () => <div>{optionalGuestlistFieldBehaviorOptions.find(option => option.value === formState.guestEmailAddressFormBehavior)?.label}</div>,
        inPageEdit: {
          // This padding is needed to prevent the row from jumping when switching to edit mode
          render: ({ onChange, value }) => <div style={{ paddingTop: 6, paddingLeft: 8 }}>
            <RadioGroup
              orientation="horizontal"
              options={cannotSetEmailAddressAsMandatory ? optionalGuestlistFieldBehaviorOptions.filter(option => option.value !== "visibleMandatory") : optionalGuestlistFieldBehaviorOptions}
              value={value}
              onChange={v => onChange(v, true)}
            />
          </div>,
          type: "custom",
          initialValue: formState.guestEmailAddressFormBehavior,
          submitChange: (email: server.dto.FormElementBehaviors) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateInstruction({ ...formState, guestEmailAddressFormBehavior: email })
          ),
        }
      }, {
        key: "mobileNumber",
        label: RESX.ContactDetails.PhoneNumber,
        render: () => <div>{optionalGuestlistFieldBehaviorOptions.find(option => option.value === formState.guestMobileNumberFormBehavior)?.label}</div>,
        inPageEdit: {
          // This padding is needed to prevent the row from jumping when switching to edit mode
          render: ({ onChange, value }) => <div style={{ paddingTop: 6, paddingLeft: 8 }}>
            <RadioGroup
              orientation="horizontal"
              options={cannotSetMobileNumberAsMandatory ? optionalGuestlistFieldBehaviorOptions.filter(option => option.value !== "visibleMandatory") : optionalGuestlistFieldBehaviorOptions}
              value={value}
              onChange={v => onChange(v, true)}
            />
          </div>,
          type: "custom",
          initialValue: formState.guestMobileNumberFormBehavior,
          submitChange: (mobileNumber: server.dto.FormElementBehaviors) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateInstruction({ ...formState, guestMobileNumberFormBehavior: mobileNumber })
          ),
        }
      }, {
        key: "passport",
        label: RESX.Passport.PassportNumber,
        render: () => <div>{optionalGuestlistFieldBehaviorOptions.find(option => option.value === formState.guestPassportNumberFormBehavior)?.label}</div>,
        inPageEdit: {
          // This padding is needed to prevent the row from jumping when switching to edit mode
          render: ({ onChange, value }) => <div style={{ paddingTop: 6, paddingLeft: 8 }}>
            <RadioGroup
              orientation="horizontal"
              options={cannotSetPassportNumberAsMandatory ? optionalGuestlistFieldBehaviorOptions.filter(option => option.value !== "visibleMandatory") : optionalGuestlistFieldBehaviorOptions}
              value={value}
              onChange={v => onChange(v, true)}
            />
          </div>,
          type: "custom",
          initialValue: formState.guestPassportNumberFormBehavior,
          submitChange: (passport: server.dto.FormElementBehaviors) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateInstruction({ ...formState, guestPassportNumberFormBehavior: passport })
          ),
        }
      }, {
        key: "notes",
        label: RESX.Note.Notes,
        render: () => <div>{optionalGuestlistFieldBehaviorOptions.find(option => option.value === formState.guestNotesFormBehavior).label}</div>,
        inPageEdit: {
          // This padding is needed to prevent the row from jumping when switching to edit mode
          render: ({ onChange, value }) => <div style={{ paddingTop: 6, paddingLeft: 8 }}>
            <RadioGroup
              orientation="horizontal"
              options={cannotSetNotesAsMandatory ? optionalGuestlistFieldBehaviorOptions.filter(option => option.value !== "visibleMandatory") : optionalGuestlistFieldBehaviorOptions}
              value={value}
              onChange={v => onChange(v, true)}
            />
          </div>,
          type: "custom",
          initialValue: formState.guestNotesFormBehavior,
          submitChange: (notes: server.dto.FormElementBehaviors) => window.CoreApi.Guestlist.UpdateGuestlistConfiguration(
            getUpdateInstruction({ ...formState, guestNotesFormBehavior: notes })
          ),
        }
      }]
    }, {
      key: "accessTypesLabel",
      label: "",
      hideLabel: true,
      render: () => <div className="flex">
        <h3 className="grow1">{RESX.Guestlist.Lists}</h3>
        <ButtonSecondary
          label={RESX.Buttons.Add}
          onClick={() => openNewAccessTypeSidePanel(data.configuration.guestlistId, () => { })}
        />
      </div>
    }, {
      key: "accessTypes",
      label: "",
      hideLabel: true,
      render: () => <AccessTypesList
        lists={data.lists}
        guestlistId={data.configuration.guestlistId}
      />
    }]}
  />
}

interface GuestlistFieldBehaviorOption {
  value: server.dto.FormElementBehaviors;
  label: string;
}

const optionalGuestlistFieldBehaviorOptions: GuestlistFieldBehaviorOption[] = [
  { value: "notVisible", label: RESX.Advancing.Hidden },
  { value: "visibleOptional", label: RESX.Advancing.Optional },
  { value: "visibleMandatory", label: RESX.Advancing.Required },
]